import {
  DATAOWNER,
  DATAOWNER_EMPLOYEES,
  DATAOWNER_KEY,
  DATAOWNER_V2_PROPERTIES,
  DATAOWNER_V2_PROPERTY_KEY,
  DATAOWNER_V2_SEARCH,
} from '@dap/memberadmin/config';
import {
  CreateDataownerDTO,
  DataownerSearchArgs,
  DataownerSearchResult,
  Dataowner,
  EmployeeBasicV2,
  PatchDataownerDTO,
  EditProperty,
  CreateProperty,
  PropertyDetail,
  PatchDataownersDTO,
} from '@dap/memberadmin/types';
import {
  dataownerDetailMapper,
  dataownerSearchArgsMapper,
  dataownerSearchResultMapper,
  employeeOverviewMapper,
  propertyMapper,
} from '@dap/common/utils';
import {
  DataownerDetailDTO,
  DataownerSearchDTO,
  EmployeeOverviewDTO,
  PropertyDetailDTO,
} from '@dap/generated-types';
import { selectBrandKey } from '../redux/brand/brandReducer';
import { RootState } from '../redux/store';
import { BrandTags, LocationTags, RoleTags } from './tags';
import { brandadminApi } from './brandadminApi';
import { DataownerTags, EmployeeTags } from './tags';
import { AddEmployeesToDataownerRequest } from '../redux/types';
import { sortAdminsFirst, sortDeactivatedLast } from '@dap/memberadmin/utils';

const tags = [
  ...Object.values(DataownerTags),
  BrandTags.BRAND_DATAOWNERS,
  EmployeeTags.EMPLOYEE,
  LocationTags.LOCATION,
];

export const dataownerApi = brandadminApi.enhanceEndpoints({ addTagTypes: tags }).injectEndpoints({
  endpoints: (build) => ({
    createDataowner: build.mutation<Dataowner, CreateDataownerDTO>({
      query: (body) => ({
        url: DATAOWNER,
        method: 'POST',
        body,
      }),
      transformResponse: dataownerDetailMapper,
      invalidatesTags: [DataownerTags.SEARCH_DATAOWNERS, BrandTags.BRAND_DATAOWNERS],
    }),
    getDataowner: build.query<
      Dataowner,
      { dataownerKey: Dataowner['key']; includeLocations?: boolean }
    >({
      query: ({ dataownerKey, includeLocations }) => {
        const url = includeLocations
          ? `${DATAOWNER_KEY(dataownerKey)}?includeLocations=true`
          : DATAOWNER_KEY(dataownerKey);
        return url;
      },
      async onQueryStarted(_key, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const state = getState() as RootState;
          // if the user has access to location but is on the wrong brand, select correct brand.
          if (state.brand.selectedBrandKey !== data.brand.key) {
            dispatch(selectBrandKey(data.brand?.key || ''));
          }
        } catch {
          /* empty */
        }
      },
      transformResponse: (response: DataownerDetailDTO) => dataownerDetailMapper(response),
      providesTags: (result, _error, { dataownerKey }) => [
        { type: DataownerTags.DATAOWNER, id: dataownerKey },
        RoleTags.ROLES,
        ...(result?.properties
          ? result.properties
              .filter((property) => property.inherited)
              .map(({ key }) => ({
                type: BrandTags.BRAND_INHERITABLE_PROPERTIES,
                id: key,
              }))
          : []),
      ],
    }),
    // patchDataowner: build.mutation<Dataowner, { dataownerKey: Dataowner['key']; payload: Partial<PatchDataownerDTO> }>({
    //   query: ({ dataownerKey, payload }) => ({
    //     url: DATAOWNER_KEY(dataownerKey),
    //     method: 'PATCH',
    //     body: payload,
    //   }),
    //   transformResponse: dataownerDetailMapper,
    //   invalidatesTags: (result, _error, arg) => [
    //     { type: DataownerTags.DATAOWNER, id: arg.dataownerKey },
    //     BrandTags.BRAND_DATAOWNERS,
    //   ],
    // }),
    deleteDataowner: build.mutation<void, Dataowner['key']>({
      query: (dataownerKey) => ({
        url: DATAOWNER_KEY(dataownerKey),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        DataownerTags.SEARCH_DATAOWNERS,
        { type: DataownerTags.DATAOWNER, id: arg },
        BrandTags.BRAND_DATAOWNERS,
      ],
    }),
    editDataownerDetails: build.mutation<
      Dataowner,
      { dataownerKey: Dataowner['key']; payload: Partial<PatchDataownerDTO> }
    >({
      query: ({ dataownerKey, payload }) => ({
        url: DATAOWNER_KEY(dataownerKey),
        method: 'PATCH',
        body: payload,
        transformResponse: dataownerDetailMapper,
      }),
      invalidatesTags: (result, _error, arg) =>
        result
          ? [
              { type: DataownerTags.DATAOWNER, id: arg.dataownerKey },
              DataownerTags.SEARCH_DATAOWNERS,
              BrandTags.BRAND_DATAOWNERS,
              ...(result.locations
                ? result.locations.map(({ key }) => ({ type: LocationTags.LOCATION, id: key }))
                : []),
            ]
          : [],
    }),
    patchDataowners: build.mutation<Dataowner, { dataowners: Array<PatchDataownersDTO> }>({
      query: ({ dataowners }) => ({
        url: DATAOWNER,
        method: 'PATCH',
        body: dataowners,
        transformResponse: (response: Array<DataownerDetailDTO>) =>
          response.map(dataownerDetailMapper),
      }),
      invalidatesTags: (result, _error, _) =>
        result
          ? [
              { type: DataownerTags.DATAOWNER },
              DataownerTags.SEARCH_DATAOWNERS,
              BrandTags.BRAND_DATAOWNERS,
              ...(result.locations
                ? result.locations.map(({ key }) => ({ type: LocationTags.LOCATION, id: key }))
                : []),
            ]
          : [],
    }),
    createDataownerProperty: build.mutation<
      PropertyDetail,
      { dataownerKey: string; property: CreateProperty }
    >({
      query: ({ dataownerKey, property }) => ({
        url: DATAOWNER_V2_PROPERTIES(dataownerKey),
        method: 'POST',
        body: property,
      }),
      transformResponse: (response: PropertyDetailDTO) => propertyMapper(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: DataownerTags.DATAOWNER, id: arg.dataownerKey },
      ],
    }),
    updateDataownerProperty: build.mutation<
      PropertyDetail,
      { dataownerKey: string; propertyKey: string; property: EditProperty }
    >({
      query: ({ dataownerKey, propertyKey, property }) => ({
        url: DATAOWNER_V2_PROPERTY_KEY(dataownerKey, propertyKey),
        method: 'PATCH',
        body: property,
      }),
      transformResponse: (response: PropertyDetailDTO) => propertyMapper(response),
      invalidatesTags: (result, _error, arg) => [
        ...(result?.inherited
          ? [
              BrandTags.BRAND_DATAOWNERS,
              { type: DataownerTags.DATAOWNER, id: arg.dataownerKey },
              { type: LocationTags.LOCATION },
              { type: BrandTags.BRAND_LOCATIONS },
              { type: BrandTags.SELECTED_BRAND },
            ]
          : [{ type: DataownerTags.DATAOWNER, id: arg.dataownerKey }]),
      ],
    }),
    deleteDataownerProperty: build.mutation<void, { dataownerKey: string; propertyKey: string }>({
      query: ({ dataownerKey, propertyKey }) => ({
        url: DATAOWNER_V2_PROPERTY_KEY(dataownerKey, propertyKey),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: DataownerTags.DATAOWNER, id: arg.dataownerKey },
      ],
    }),
    searchDataowners: build.query<Array<DataownerSearchResult>, DataownerSearchArgs>({
      query: (props) => ({
        url: props.includeLocations
          ? DATAOWNER_V2_SEARCH + '?includeLocations=true'
          : DATAOWNER_V2_SEARCH,
        method: 'POST',
        body: dataownerSearchArgsMapper(props),
      }),
      providesTags: [DataownerTags.SEARCH_DATAOWNERS],
      transformResponse: (response: Array<DataownerSearchDTO>) =>
        response.map(dataownerSearchResultMapper),
    }),
    getDataownerEmployees: build.query<
      Array<EmployeeBasicV2>,
      { dataownerKey: Dataowner['key']; includeLocation?: boolean }
    >({
      query: ({ dataownerKey, includeLocation }) => {
        const url = includeLocation
          ? `${DATAOWNER_EMPLOYEES(dataownerKey)}?includeLocations=true`
          : `${DATAOWNER_EMPLOYEES(dataownerKey)}`;
        return url;
      },
      transformResponse: (response: EmployeeOverviewDTO, _meta, args) => {
        const mappedResponse = employeeOverviewMapper(response);
        const mergedEmployees = [
          ...mappedResponse.activeEmployees,
          ...mappedResponse.inactiveEmployees,
        ];
        return sortDeactivatedLast(sortAdminsFirst(mergedEmployees));
      },
      providesTags: (result, _error, { dataownerKey }) =>
        result
          ? [
              ...result.map(({ userId }) => ({ type: EmployeeTags.EMPLOYEE, id: userId })),
              { type: DataownerTags.EMPLOYEES, id: dataownerKey },
              { type: DataownerTags.DATAOWNER, id: dataownerKey },
              RoleTags.ROLES,
            ]
          : [{ type: DataownerTags.EMPLOYEES, id: dataownerKey }, RoleTags.ROLES],
    }),
    addDataownerEmployee: build.mutation<EmployeeBasicV2[], AddEmployeesToDataownerRequest>({
      query: ({ dataownerKey, includeLocations, ...rest }) => ({
        url: includeLocations
          ? `${DATAOWNER_EMPLOYEES(dataownerKey)}?includeLocations=true`
          : DATAOWNER_EMPLOYEES(dataownerKey),
        method: 'POST',
        body: [rest], // TODO: Use addEmployeeMapper after MID-901
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: DataownerTags.EMPLOYEES, id: arg.dataownerKey },
        { type: DataownerTags.SEARCH_DATAOWNERS },
        { type: EmployeeTags.SEARCH_EMPLOYEES },
        { type: EmployeeTags.EMPLOYEE, id: arg.userId },
      ],
    }),
    removeDataownerEmployee: build.mutation<
      void,
      { dataownerKey: string; userId: EmployeeBasicV2['userId'] }
    >({
      query: ({ dataownerKey, userId }) => ({
        url: `${DATAOWNER_EMPLOYEES(dataownerKey)}/${userId}`,
        method: 'DELETE',
      }),
      // is invalidated directly when successful in RemoveDataownerEmployee
      invalidatesTags: [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateDataownerMutation,
  useGetDataownerQuery,
  useDeleteDataownerMutation,
  useEditDataownerDetailsMutation,
  usePatchDataownersMutation,
  useCreateDataownerPropertyMutation,
  useUpdateDataownerPropertyMutation,
  useDeleteDataownerPropertyMutation,
  useSearchDataownersQuery,
  useGetDataownerEmployeesQuery,
  useAddDataownerEmployeeMutation,
  useRemoveDataownerEmployeeMutation,
} = dataownerApi;
